'use client';

import React, { useState, useEffect, useRef } from "react";
import { Globe, Info } from "@phosphor-icons/react";
import TextInputField from "@/components/ui/TextInputField/TextInputField";
import TextAreaField from "@/components/ui/TextAreaField/TextAreaField";
import SelectableLabelTiles from "@/components/ui/SelectableLabelTiles/SelectableLabelTiles";
import { LanguageType, LanguageDirection } from "@/lib/types";
import DropdownField from "@/components/ui/DropdownField/DropdownField";
import ScrollTrigger from "gsap/ScrollTrigger";
import { SubmitFormButton } from "@/components/ui/SubmitFormButton";
import PrimaryButton from "@/components/ui/PrimaryButton/PrimaryButton";
import IconWrapper from "@/components/ui/IconWrapper/IconWrapper";
import {
  createEmailChangeHandler,
  createPhoneChangeHandler,
  createRequiredFieldChangeHandler,
  createTextAreaChangeHandler,
  submitToZoho,
  checkAllMandatoryFields,
  checkFormValidationErrors,
  VALIDATION_HINTS,
  VALIDATION_HINTS_AR
} from "@/lib/formUtils";
import { FOUNDING_MEMBERSHIPS_TAKEN, FOUNDING_MEMBERSHIPS_TOTAL } from "@/lib/variables";
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigation";
import { useLenis } from "@/contexts/LenisContext";
import { completeRegistration, newMetaEventId } from "@/lib/gtm";
import { useIsLg } from '@/contexts/BreakpointContext';

interface SignUpSectionProps {
  language?: LanguageType
  id?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


interface FormData {
  Email: string;
  Last_Name: string;
  First_Name: string;
  Phone_number: string;
  Nationality: string;
  Gender: string;
  Preferred_Contact_Time: string;
  Preferred_Language: string;
  Desired_Service: string;
  Lead_Source: string;
  Referred_by_client_name: string;
  Health_Conditions: string;
  Personal_Notes: string;
  Signup_Type: string;
}

const SignUpSection: React.FC<SignUpSectionProps> = ({
  language = "english",
  id = "signup-section"
}) => {
  const { lenis } = useLenis();
  const isLg = useIsLg();
  const animatedNavigate = useAnimatedNavigate();
  const [selectedCard, setSelectedCard] = useState<'waitlist' | 'founding-member' | null>(null);
  const revealedSectionRef = useRef<HTMLDivElement>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    Email: "",
    Last_Name: "",
    First_Name: "",
    Phone_number: "",
    Nationality: "",
    Gender: "",
    Preferred_Contact_Time: "",
    Preferred_Language: "",
    Lead_Source: "",
    Desired_Service: "",
    Referred_by_client_name: "",
    Health_Conditions: "",
    Personal_Notes: "",
    Signup_Type: "",
  });

  // Validation states
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isPhoneInvalid, setIsPhoneInvalid] = useState(false);
  const [isFirstNameInvalid, setIsFirstNameInvalid] = useState(false);
  const [isLastNameInvalid, setIsLastNameInvalid] = useState(false);
  const [isDesiredServiceInvalid, setIsDesiredServiceInvalid] = useState(false);
  const [isHealthConditionsInvalid, setIsHealthConditionsInvalid] = useState(false);
  const [isPersonalNotesInvalid, setIsPersonalNotesInvalid] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Create handlers using utilities
  const handleEmailChange = createEmailChangeHandler(setFormData, setIsEmailInvalid, 'Email');
  const handlePhoneChange = createPhoneChangeHandler(setFormData, setIsPhoneInvalid, 'Phone_number');
  const handleFirstNameChange = createRequiredFieldChangeHandler(setFormData, setIsFirstNameInvalid, 'First_Name');
  const handleLastNameChange = createRequiredFieldChangeHandler(setFormData, setIsLastNameInvalid, 'Last_Name');
  // const handleDesiredServiceChange = createRequiredFieldChangeHandler(setFormData, setIsDesiredServiceInvalid, 'Desired_Service');
  const handleHealthConditionsChange = createTextAreaChangeHandler(setFormData, setIsHealthConditionsInvalid, 'Health_Conditions', formData.Health_Conditions);
  const handlePersonalNotesChange = createTextAreaChangeHandler(setFormData, setIsPersonalNotesInvalid, 'Personal_Notes', formData.Personal_Notes);

  // Check if all mandatory fields have values
  const hasAllMandatoryFields = checkAllMandatoryFields(formData, [
    'Email',
    'Last_Name',
    'First_Name',
    'Phone_number',
    'Nationality',
    'Preferred_Contact_Time',
    'Preferred_Language',
    'Desired_Service',
    'Lead_Source'
  ]);

  // Check if form has any validation errors
  const hasValidationErrors = checkFormValidationErrors(
    {
      email: isEmailInvalid,
      firstName: isFirstNameInvalid,
      lastName: isLastNameInvalid,
      phone: isPhoneInvalid,
      desiredService: isDesiredServiceInvalid,
      healthConditions: isHealthConditionsInvalid,
      personalNotes: isPersonalNotesInvalid
    },
    hasAllMandatoryFields
  );

  // Submit handler
  const handleSubmit = async () => {
    if (hasValidationErrors) return;

    setSubmitStatus('submitting');
    const metaEvent = { event: 'complete_registration' as const, eventId: newMetaEventId('complete_registration') };
    const result = await submitToZoho(formData, 'Website - Signup', metaEvent);

    if (result.success) {
      completeRegistration(formData.Email, formData.Phone_number, metaEvent.eventId);
      animatedNavigate(result.leadToken ? `/thank-you?t=${encodeURIComponent(result.leadToken)}` : '/thank-you');
      // setSubmitStatus('success');
    } else {
      setSubmitStatus('error');
    }
  };

  const handleResetForm = () => {
    // Reset form data to initial state
    setFormData({
      Email: "",
      Last_Name: "",
      First_Name: "",
      Phone_number: "",
      Nationality: "",
      Gender: "",
      Preferred_Contact_Time: "",
      Preferred_Language: "",
      Lead_Source: "",
      Desired_Service: "",
      Referred_by_client_name: "",
      Health_Conditions: "",
      Personal_Notes: "",
      Signup_Type: "",
    });

    // Reset all validation states
    setIsEmailInvalid(false);
    setIsPhoneInvalid(false);
    setIsFirstNameInvalid(false);
    setIsLastNameInvalid(false);
    setIsDesiredServiceInvalid(false);
    setIsHealthConditionsInvalid(false);
    setIsPersonalNotesInvalid(false);

    // Reset submit status
    setSubmitStatus('idle');

    // Reset card selection
    setSelectedCard(null);
  };


  // Add useEffect to refresh ScrollTrigger when revealed section changes
  useEffect(() => {
    // Small delay to ensure the animation has started
    const timeoutId = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 600); // Slightly longer than the animation duration (500ms)
    // TODO: shouldnt this also take into account the delay of the accordion?

    return () => clearTimeout(timeoutId);
  }, [selectedCard]); // Refresh when selection changes

  // Reset submission status when form data changes
  useEffect(() => {
    if (submitStatus !== 'idle' && submitStatus !== 'submitting') {
      setSubmitStatus('idle');
    }
  }, [formData]);

  const handleCardSelect = (card: 'waitlist' | 'founding-member') => {
    const newSelection = selectedCard === card ? null : card;
    setSelectedCard(newSelection);
    const signupType = newSelection === 'waitlist' ? 'Waitlist' : newSelection === 'founding-member' ? 'Founding Member' : '';
    setFormData(prev => ({ ...prev, Signup_Type: signupType }));

    // Scroll to the revealed section after the reveal transition
    if (newSelection && revealedSectionRef.current && lenis) {
      setTimeout(() => {
        if (revealedSectionRef.current) {
          lenis.scrollTo(revealedSectionRef.current, { offset: -100, duration: 2 });
        }
      }, 500);
    }
  };

  const isRevealed = selectedCard !== null;

  return (
    <section
      id={id}
      style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
      className="
        w-full flex items-center justify-center
        padding-main-x-mobile
        padding-main-x-desktop
      ">
      <div className="w-full container-x-small">

        {/* This gap for the flex col is moved as (conditional) padding top for the submit button section to preserve the form reveal animation */}
        <div
        //  gap-y-[2.5rem] lg:gap-y-[3.5rem]
          className="w-full flex flex-col
          ">
          <div className="w-full flex flex-col gap-y-[0.25rem] lg:gap-y-[1.5rem]">
            <div className="grid grid-cols-1 tb:grid-cols-2 gap-x-[1rem] gap-y-[0.25rem] tb:gap-y-[1.5rem] px-[0.5rem] tb:px-0">

              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={language === 'arabic' ? "الاسم الأول" : "First name"}
                    placeholder={language === 'arabic' ? "أدخل اسمك الأول" : "Your first name"}
                    value={formData.First_Name}
                    onChange={handleFirstNameChange}
                    invalid={isFirstNameInvalid}
                    hint={isFirstNameInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.FIRST_NAME_REQUIRED : VALIDATION_HINTS.FIRST_NAME_REQUIRED) : undefined}
                  />
              </div>
              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <TextInputField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "اسم العائلة" : "Last name"}
                  placeholder={language === 'arabic' ? "أدخل اسم عائلتك" : "Your last name"}
                  value={formData.Last_Name}
                  onChange={handleLastNameChange}
                  invalid={isLastNameInvalid}
                  hint={isLastNameInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.LAST_NAME_REQUIRED : VALIDATION_HINTS.LAST_NAME_REQUIRED) : undefined}
                />
              </div>
              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <TextInputField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "البريد الإلكتروني" : "Email address"}
                  placeholder={language === 'arabic' ? "عنوان بريدك الإلكتروني للتواصل" : "Your contact email"}
                  value={formData.Email}
                  onChange={handleEmailChange}
                  invalid={isEmailInvalid}
                  hint={isEmailInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.EMAIL_INVALID : VALIDATION_HINTS.EMAIL_INVALID) : undefined}
                />
              </div>
              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <TextInputField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "رقم الهاتف" : "Phone number"}
                  placeholder={language === 'arabic' ? "رقم هاتفك" : "Your phone number"}
                  value={formData.Phone_number}
                  onChange={handlePhoneChange}
                  invalid={isPhoneInvalid}
                  hint={isPhoneInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.PHONE_INVALID : VALIDATION_HINTS.PHONE_INVALID) : undefined}
                />
              </div>

              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <DropdownField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "الجنسية" : "Nationality"}
                  placeholder={language === 'arabic' ? "اختر جنسيتك" : "Your nationality"}
                  value={formData.Nationality}
                  options={[
                    { label: language === 'arabic' ? "السعوديه" : "Saudi Arabia", value: "Saudi Arabia", icon: <img src="/Flags/Saudi Arabia.svg" alt="Saudi Arabia flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "الإمارات" : "United Arab Emirates (UAE)", value: "United Arab Emirates (UAE)", icon: <img src="/Flags/United Arab Emirates.svg" alt="UAE flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "الكويت" : "Kuwait", value: "Kuwait", icon: <img src="/Flags/Kuwait.svg" alt="Kuwait flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "قطر" : "Qatar", value: "Qatar", icon: <img src="/Flags/Qatar.svg" alt="Qatar flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "البحرين" : "Bahrain", value: "Bahrain", icon: <img src="/Flags/Bahrain.svg" alt="Bahrain flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "عمان" : "Oman", value: "Oman", icon: <img src="/Flags/Oman.svg" alt="Oman flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "مصر" : "Egypt", value: "Egypt", icon: <img src="/Flags/Egypt.svg" alt="Egypt flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "أوروبا" : "Europe", value: "Europe", icon: <img src="/Flags/European Union.svg" alt="European Union flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "أمريكا" : "America", value: "America", icon: <img src="/Flags/United States.svg" alt="United States flag" style={{ width: '20px', height: '20px' }} /> },
                    { label: language === 'arabic' ? "آسيا" : "Asia", value: "Asia", icon: <Globe /> },
                    { label: language === 'arabic' ? "غير ذلك" : "Other", value: "Other", icon: <Globe /> },
                  ]}
                  onChange={(value) => setFormData(prev => ({ ...prev, Nationality: value }))}
                />
              </div>
              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <DropdownField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "نوع الجنس" : "Gender"}
                  placeholder={language === 'arabic' ? "اختر الجنس" : "Your gender"}
                  value={formData.Gender}
                  options={[
                    { label: language === 'arabic' ? "أنثى" : "Female", value: "Female" },
                    { label: language === 'arabic' ? "ذكر" : "Male", value: "Male" },
                  ]}
                  onChange={(value) => setFormData(prev => ({ ...prev, Gender: value }))}
                />
              </div>
            </div>

            <div className="w-full grid grid-cols-1 tb:grid-cols-2 gap-[0.625rem] tb:gap-[1rem] pb-[2rem] tb:pb-[1rem]">
              <div style={{ direction: "ltr" }} className="pt-[0.75rem] w-full  tb:flex-grow flex flex-col gap-[0.625rem] tb:gap-[1.25rem]">
                <SelectableLabelTiles
                  label={language === 'arabic' ? "صمم للجميع" : "DESIGNED FOR EVERYONE"}
                  text={language === 'arabic' ? "ارغب بالانضمام لقائمة الانتظار" : "I want to join the waitlist"}
                  onClick={() => handleCardSelect('waitlist')}
                  isSelected={selectedCard === 'waitlist'}
                />
                <div style={{ direction: LANGUAGE_TO_DIRECTION[language] }} className="w-full flex flex-row gap-[0.5rem] items-start justify-start px-[0.5rem]">

                  <div className="p-0 lg:p-[0.125rem]">
                    <IconWrapper size={20} className="text-graphics-quaternary">
                      <Info />
                    </IconWrapper>
                  </div>

                  <p className="text-16-to-14--alexandria text-text-tertiary" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                    {language === 'arabic' ? "لا توجد متطلبات خاصة. يتناسب جداً مع من يبحث عن عيش التجربة مبكراً لنهجنا التحويلي." : "No special requirements! Ideal for those seeking an early experience of our transformative approach."}
                  </p>

                </div>

                {/* <TextAccordion
                  size={16}
                  language={language}
                  title='Dropdown'
                  options={[
                    { label: "Dropdown item 1", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 1. " },
                    { label: "Dropdown item 2", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 2. " },
                    { label: "Dropdown item 3", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 3. " },
                    { label: "Dropdown item 4", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 4. " },
                    { label: "Dropdown item 5", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 5. " },
                    { label: "Dropdown item 6", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 6. " },
                    { label: "label9", value: "value9" }]}
                >
                  <Info size={1.25 * rem} />
                </TextAccordion> */}
              </div>

              <div style={{ direction: "ltr" }} className="pt-[0.75rem] w-full  tb:flex-grow flex flex-col gap-[0.625rem] tb:gap-[1.25rem]">
                <SelectableLabelTiles
                  label={language === 'arabic' ? `حصرياً. متبقي فقط ${FOUNDING_MEMBERSHIPS_TOTAL - FOUNDING_MEMBERSHIPS_TAKEN} عضوية` : `LIMITED. ${FOUNDING_MEMBERSHIPS_TOTAL - FOUNDING_MEMBERSHIPS_TAKEN}/${FOUNDING_MEMBERSHIPS_TOTAL} SPOTS LEFT`}
                  text={language === 'arabic' ? "ارغب في الحصول على عضوية التأسيس" : "I want to become a Founding Member"}
                  onClick={() => handleCardSelect('founding-member')}
                  isSelected={selectedCard === 'founding-member'}
                />

                <div style={{ direction: LANGUAGE_TO_DIRECTION[language] }} className="w-full flex flex-row gap-[0.5rem] items-start justify-start px-[0.5rem]">

                  <div className="p-0 lg:p-[0.125rem]">
                    <IconWrapper size={20} className="text-graphics-quaternary">
                      <Info />
                    </IconWrapper>
                  </div>

                  <p className="text-16-to-14--alexandria text-text-tertiary" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                    {language === 'arabic' ? "الالتزام المطلوب لباقة البريميوم/ VIP. مخصص لنخبة مختارة من الاعضاء المستعدين لاحتضان تحولهم الكامل والاستفادة من الخدمات مدى الحياة." : "Premium/VIP commitment required. For the select few ready to embrace their full transformation and unlock lifetime privileges."}
                  </p>

                </div>
                {/* <TextAccordion
                  language={language}
                  size={16}
                  disabled={true}
                  title='Dropdown'
                  options={[
                    { label: "Dropdown item 1", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 1. " },
                    { label: "Dropdown item 2", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 2. " },
                    { label: "Dropdown item 3", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 3. " },
                    { label: "Dropdown item 4", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 4. " },
                    { label: "Dropdown item 5", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 5. " },
                    { label: "Dropdown item 6", value: "An introductory session can be booked within a maximum of 7 days from the date of purchase 6. " },
                    { label: "label9", value: "value9" }]}
                >
                  <Info size={1.25 * rem} />
                </TextAccordion> */}
              </div>

            </div>

            <div ref={revealedSectionRef} />
            <div className={`
              grid grid-cols-1 tb:grid-cols-2 gap-y-[0.25rem] tb:gap-y-[1.5rem] tb:gap-x-[1rem] px-[0.5rem] tb:px-0
              transition-all duration-500 ease-in-out
              overflow-hidden
              ${isRevealed
                ? 'opacity-100 translate-y-0 max-h-fit'
                : 'opacity-0 translate-y-[-20px] max-h-0'
                // i cannot use 'hidden' because it messes up the ease-reveal animation
                // so the gap between the mini-sections of the form will act as the top
                // gap for the submit button when the last mini-section is not revealed
              }
            `}>
              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <DropdownField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "الوقت المفضل للتواصل" : "Preferred contact time"}
                  placeholder={language === 'arabic' ? "أفضل وقت لاتصال فريقنا بكم:" : "Best time for us to call you"}
                  value={formData.Preferred_Contact_Time}
                  options={[
                    { label: language === 'arabic' ? "10 ص – 12 م" : "10 AM – 12 PM", value: "10 AM – 12 PM" },
                    { label: language === 'arabic' ? "12 م – 4 م" : "12 PM – 4 PM", value: "12 PM – 4 PM" },
                    { label: language === 'arabic' ? "4 م – 8 م" : "4 PM – 8 PM", value: "4 PM – 8 PM" },
                  ]}
                  onChange={(value) => setFormData(prev => ({ ...prev, Preferred_Contact_Time: value }))}
                />
              </div>
              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <DropdownField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "اللغة المفضلة" : "Preferred language"}
                  placeholder={language === 'arabic' ? "اختاروا لغتكم المفضلة:" : "Your preferred language"}
                  value={formData.Preferred_Language}
                  options={[
                    { label: language === 'arabic' ? "الإنجليزية" : "English", value: "English" },
                    { label: language === 'arabic' ? "العربية" : "Arabic", value: "Arabic" },
                  ]}
                  onChange={(value) => setFormData(prev => ({ ...prev, Preferred_Language: value }))}
                />
              </div>

              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <DropdownField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "الخدمة المطلوبة" : "Desired service"}
                  placeholder={language === 'arabic' ? "أخبرونا بالخدمة التي ترغبون في تعزيزها:" : "Your beauty goals"}
                  value={formData.Desired_Service}
                  options={[
                    { label: language === 'arabic' ? "الوجه" : "Face", value: "Face" },
                    { label: language === 'arabic' ? "الجسم" : "Body", value: "Body" },
                    { label: language === 'arabic' ? "الوجه والجسم" : "Face & Body", value: "Face & Body" },
                    { label: language === 'arabic' ? "الاستشفاء" : "Recovery", value: "Recovery" },
                  ]}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, Desired_Service: value }));
                    setIsDesiredServiceInvalid(false);
                  }}
                />
              </div>
              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <DropdownField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "كيف اكتشفتم ريڤيڤ؟" : "How did you discover Rivive?"}
                  placeholder={language === 'arabic' ? "اختر مصدر معرفتك بنا:" : "Your source of referral"}
                  value={formData.Lead_Source}
                  options={[
                    { label: language === 'arabic' ? "الأصدقاء والعائلة" : "Friends & family", value: "Friends & family" },
                    { label: language === 'arabic' ? "فيسبوك" : "Facebook", value: "Facebook" },
                    { label: language === 'arabic' ? "إنستغرام" : "Instagram", value: "Instagram" },
                    { label: language === 'arabic' ? "جوجل" : "Google", value: "Google" },
                    { label: language === 'arabic' ? "تيك توك" : "Tiktok", value: "Tiktok" },
                    { label: language === 'arabic' ? "منصات الذكاء الاصطناعي" : "AI platforms", value: "AI platforms" },
                    { label: language === 'arabic' ? "لينكد إن" : "Linkedin", value: "Linkedin" },
                    { label: language === 'arabic' ? "أخرى" : "Other", value: "Other" },
                  ]}
                  onChange={(value) => setFormData(prev => ({ ...prev, Lead_Source: value }))}
                />
              </div>
              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 ">
                <TextInputField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "إجابة أخرى (اختياري)" : "Referred by (optional)"}
                  placeholder={language === 'arabic' ? "أدخلوا اسم الشخص الذي أحلاكم إلينا" : "Your referrer's name"}
                  value={formData.Referred_by_client_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, Referred_by_client_name: e.target.value }))}
                  optional={true}
                />
              </div>

              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 tb:col-span-2">
                <TextAreaField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "الحالات الصحية (اختياري)" : "Health conditions (optional)"}
                  placeholder={language === 'arabic' ? "شاركوا الحالات الصحية التي قد تؤثر على العلاج" : "Share health conditions that may affect treatment"}
                  value={formData.Health_Conditions}
                  onChange={handleHealthConditionsChange}
                  invalid={isHealthConditionsInvalid}
                  hint={isHealthConditionsInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.CHARACTER_LIMIT_REACHED : VALIDATION_HINTS.CHARACTER_LIMIT_REACHED) : undefined}
                  optional={true}
                />
              </div>

              <div style={{ direction: "ltr" }} className="flex-grow col-span-1 tb:col-span-2">
                <TextAreaField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "ملاحظات شخصية (اختياري)" : "Personal notes (optional)"}
                  placeholder={language === 'arabic' ? "اكتبوا أي معلومات إضافية ترغبون في مشاركتها معنا" : "Write anything else you'd like to share with us"}
                  value={formData.Personal_Notes}
                  onChange={handlePersonalNotesChange}
                  invalid={isPersonalNotesInvalid}
                  hint={isPersonalNotesInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.CHARACTER_LIMIT_REACHED : VALIDATION_HINTS.CHARACTER_LIMIT_REACHED) : undefined}
                  optional={true}
                />
              </div>
            </div>
          </div>


          <div
            // ${
            // //isRevealed
            // true
            //   ? 'opacity-100 translate-y-0 max-h-fit'
            //   : 'opacity-0 translate-y-[-20px] max-h-0 '
            // }
           className={`
            w-full flex flex-col gap-y-[2.5rem] lg:gap-y-[4.5rem]
            transition-all duration-500 ease-in-out delay-200
            overflow-visible
            opacity-100 translate-y-0 max-h-fit
            ${
            isRevealed
              ? 'pt-[0.25rem] lg:pt-[1.5rem]'
              : 'pt-0'
            }

          `}>
            <div className="p-1 overflow-visible">
              <SubmitFormButton
                style="accent"
                isLg={isLg}
                isError={submitStatus === 'error'}
                language={language}
                buttonText={
                  submitStatus === 'success'
                    ? (language === 'arabic' ? "تم الارسال بنجاح" : "You're on the list!")
                    : submitStatus === 'error'
                      ? (language === 'arabic' ? "تعذر الارسال" : "Unable to reserve")
                      : (language === 'arabic' ? "انضم إلى ريڤيڤ" : "Secure your place")
                }
                wrapperText={
                  submitStatus === 'success'
                    ? (language === 'arabic' ? "شكرًا لاهتمامك. سيتواصل معك أحد مختصّينا قريبًا لمواصلة رحلتك معنا." : "Reserved successfully. Our team will reach out shortly to confirm your status and guide your entry.")
                    : submitStatus === 'error'
                      ? (language === 'arabic' ? "تعذّر إرسال طلبك. يُرجى المحاولة مرة أخرى أو التواصل معنا مباشرة." : "Something went wrong. Please refresh and try again.")
                      : ''
                }
                wrapperBgColor={
                  submitStatus === 'success'
                    ? 'bg-greens-green-2'
                    : submitStatus === 'error'
                      ? 'bg-state-error-base'
                      : 'bg-greens-green-2'
                }
                privacyText={
                  language === 'arabic'
                    ? "بإرسال هذا النموذج، فإنك توافق على سياسة الخصوصية الخاصة بنا. نحن نتعامل مع معلوماتك الشخصية بالعناية نفسها التي نوليها لكل علاج نقدّمه."
                    : "By submitting this form, you agree to our Privacy Policy. We treat your personal information with the same care we bring to every treatment."
                }
                resetButtonText={language === 'arabic' ? "إعادة تعيين النموذج" : "Reset the form"}
                onClick={handleSubmit}
                onReset={handleResetForm}
                disabled={hasValidationErrors}
                loading={submitStatus === 'submitting'}
              />
            </div>


            <div className="w-full h-[1.5px] min-h-[1.5px] bg-stroke-soft-200 rounded" />

            <div className="w-full flex items-center justify-center px-[0.5rem] lg:px-0">

              <div className="w-full max-w-[43.3125rem] flex flex-col gap-y-[2.5rem] lg:gap-y-[3.5rem] items-center justify-center">

                <div className="w-full flex flex-col gap-y-[1.5rem] lg:gap-y-[2rem] text-center">
                  <p className="text-28-to-20--neue-june text-text-primary" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                    {language === 'arabic' ? `انضم الى قصة تأسيس ريڤيڤ. لم يتبقى سوى ${FOUNDING_MEMBERSHIPS_TOTAL - FOUNDING_MEMBERSHIPS_TAKEN} عضويه تأسيسية.` : `Become part of Rivive's founding story. Just ${FOUNDING_MEMBERSHIPS_TOTAL - FOUNDING_MEMBERSHIPS_TAKEN}/${FOUNDING_MEMBERSHIPS_TOTAL} Founding Memberships remain.`}
                  </p>
                  <div className="w-full flex flex-col gap-y-[0.5rem] items-center justify-center gap-y-[0.75rem] lg:gap-y-[1.25rem]">
                    <p className="text-18--alexandria text-text-tertiary" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                      {language === 'arabic' ? "العضوية التأسيسه هي دعوه للاستمتاع بمميزات تدوم للابد. هذه الفرصة النادرة متوفرة فقط لأول خمسين شخص ولن تتكرر ابداً." : "Founding Memberships are your invitation to privileges that last forever. Reserved for only 50 members, this rare opportunity will never again return."}
                    </p>
                    <p className="text-14-fixed--alexandria text-text-quaternary" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                      {language === 'arabic' ? "*الالتزام المطلوب لباقة البريميوم/ VIP بعد التقييم." : "*Premium/VIP commitment required post-evaluation."}
                    </p>
                  </div>
                </div>


                {/* Progress Bar */}
                <div className="w-full relative mb-[0.75rem]">
                  {/* Track */}
                  <div
                    className="w-full h-[6px] lg:h-[8px] rounded-full"
                    style={{ backgroundColor: '#BFAD9832' }}
                  />
                  {/* Progress Indicator */}
                  <div
                    className={`absolute top-[-1px] ${language === 'arabic' ? 'right-0' : 'left-0'} h-[6px] lg:h-[10px] rounded-full`}
                    style={{
                      backgroundColor: '#E89833',
                      width: `${Math.round((FOUNDING_MEMBERSHIPS_TAKEN / FOUNDING_MEMBERSHIPS_TOTAL) * 100)}%`
                    }}
                  />
                </div>
                {/* Become a Member Button */}
                <div className="w-fit pb-[1rem]">
                  <PrimaryButton
                    text={language === 'arabic' ? "انضم للمؤسسين" : "Become a founder"}
                    type="accent"
                    size={isLg ? 56 : 52}
                    language={language}
                    onClick={() => {
                      const section = document.getElementById(id);
                      if (section) {
                        // Emit event to hide navbar during smooth scroll
                        window.dispatchEvent(new CustomEvent('lenisScrollStart'));

                        const y = section.getBoundingClientRect().top + window.scrollY;

                        lenis?.scrollTo(y, {
                          offset: 0,
                          duration: 2,
                          easing: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
                          immediate: false,
                          lock: true,
                          onComplete: () => {
                            // Re-enable user scroll and navbar
                            window.dispatchEvent(new CustomEvent('lenisScrollEnd'));
                          }
                        });
                      }
                    }}
                  />
                </div>



              </div>

            </div>


          </div>

        </div >
      </div >
    </section >
  );
}


export default SignUpSection
